/**
 * SubmissionError Component
 *
 * Error state for the submission page handling 7 error types:
 * - not-found: Link doesn't exist
 * - inactive: Investor paused submissions
 * - full: Inbox full
 * - rate-limit: Too many submissions
 * - file-error: Invalid file
 * - server-error: Backend failure
 * - generic: Fallback with custom message
 */

import { View, Text } from 'react-native';
import { openExternalUrl } from '@/lib/linking';
import { AlertTriangle } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { usePublicThemeColors } from '@/lib/useThemeColors';

// ============================================================================
// Types
// ============================================================================

export type SubmissionErrorType =
  | 'not-found'
  | 'inactive'
  | 'full'
  | 'rate-limit'
  | 'file-error'
  | 'server-error'
  | 'generic';

interface SubmissionErrorProps {
  /** Error type determines which message to show */
  errorType: SubmissionErrorType;
  /** Raw error message (for generic type) */
  errorMessage?: string;
  /** Retry callback */
  onRetry?: () => void;
  /** Investor slug (for context) */
  slug?: string;
}

// ============================================================================
// Error Config
// ============================================================================

interface ErrorConfig {
  title: string;
  message: string;
  retryable: boolean;
}

function getErrorConfig(
  errorType: SubmissionErrorType,
  errorMessage?: string
): ErrorConfig {
  switch (errorType) {
    case 'not-found':
      return {
        title: 'Link Not Found',
        message:
          "This submission link doesn't exist. Please check the URL or contact the investor directly.",
        retryable: false,
      };
    case 'inactive':
      return {
        title: 'Submissions Paused',
        message:
          'This investor has temporarily paused new submissions. Please try again later.',
        retryable: false,
      };
    case 'full':
      return {
        title: 'Inbox Full',
        message:
          "This investor's submission inbox is currently full. Please try again later.",
        retryable: false,
      };
    case 'rate-limit':
      return {
        title: 'Too Many Submissions',
        message:
          "You've submitted too many decks recently. Please wait a moment and try again.",
        retryable: true,
      };
    case 'file-error':
      return {
        title: 'File Error',
        message:
          "There was a problem with your file. Please ensure it's a valid PDF under 20MB.",
        retryable: true,
      };
    case 'server-error':
      return {
        title: 'Something Went Wrong',
        message:
          "We're having trouble processing your submission. Please try again in a few minutes.",
        retryable: true,
      };
    case 'generic':
    default:
      return {
        title: 'Submission Failed',
        message: errorMessage || 'An unexpected error occurred.',
        retryable: true,
      };
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function SubmissionError({
  errorType,
  errorMessage,
  onRetry,
  slug,
}: SubmissionErrorProps) {
  const colors = usePublicThemeColors();
  const config = getErrorConfig(errorType, errorMessage);

  return (
    <Animated.View
      entering={FadeInDown.duration(400)}
      style={{ alignItems: 'center', width: '100%' }}
    >
      <View
        className="w-full p-6 rounded-2xl items-center"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Error Icon */}
        <View
          className="w-20 h-20 rounded-full items-center justify-center mb-5"
          style={{ backgroundColor: colors.red.light }}
        >
          <AlertTriangle size={36} color={colors.red.main} />
        </View>

        {/* Title */}
        <Text
          className="text-lg font-bold mb-2 text-center"
          style={{ color: colors.text.primary }}
        >
          {config.title}
        </Text>

        {/* Message */}
        <Text
          className="text-sm text-center leading-relaxed mb-6"
          style={{ color: colors.text.secondary }}
        >
          {config.message}
        </Text>

        {/* Retry Button */}
        {config.retryable && onRetry && (
          <AnimatedPressable
            onPress={onRetry}
            className="px-8 py-3 rounded-xl mb-4"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text className="text-base font-semibold" style={{ color: '#FFFFFF' }}>
              Try Again
            </Text>
          </AnimatedPressable>
        )}

        {/* Support Link */}
        <AnimatedPressable
          onPress={() => openExternalUrl('mailto:support@inverio.ai', 'SubmissionError.email')}
        >
          <Text
            className="text-xs text-center"
            style={{ color: colors.text.tertiary }}
          >
            Need help?{' '}
            <Text style={{ color: colors.primary[600], textDecorationLine: 'underline' }}>
              Contact support@inverio.ai
            </Text>
          </Text>
        </AnimatedPressable>
      </View>
    </Animated.View>
  );
}
